import { ChartPermission, ErrorCard, ErrorFormField } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem, ELSIcon } from 'components/common/els';
import { FormFieldInputType, ValidationActionType } from 'models/enum';
import { ChartViewProps } from 'components/features/shared/constants';
import { ChartMetaFormField } from 'models/ui';
import { FIELD_CONFIG_MAP, FOCUSED_HISTORY_DETAILS_MAP, FormField, getFocusedHistoryParentField, OBTriageFieldConfig, STEP_CONFIGS, ViewTitle } from './constants';
import OBTriageDateField from './OBTriageDateField';
import OBTriageTimeField from './OBTriageTimeField';
import './ob.triage.scss';

export interface OBTriageViewProps extends ChartViewProps {
  activeStep: number;
  canContinue: boolean;
  completedSteps?: boolean[];
  displayAuthoringData?: Function;
  enableDisplayRecordsButton?: boolean;
  maxSelectableStep: number;
  isAuthor: boolean;
  onBackStep: VoidFunction;
  onContinueStep: VoidFunction;
  onSelectStep: (stepIndex: number) => void;
}

const UNIFORM_FIELD_WIDTH = '1o3';

const getFieldConfig = (fieldName: FormField): OBTriageFieldConfig => FIELD_CONFIG_MAP.get(fieldName);

const getTrimmedChangeEvent = (arg: unknown, maxLength: number) => {
  const nextValue = String((arg as { target?: { value?: unknown } })?.target?.value ?? '');
  if (nextValue.length <= maxLength) return arg;

  return {
    ...(arg as Record<string, unknown>),
    target: {
      ...((arg as { target?: Record<string, unknown> })?.target ?? {}),
      value: nextValue.slice(0, maxLength)
    }
  };
};

const getVisibleErrors = (formField: ChartMetaFormField | undefined, formSubmittedCount: number) => {
  const errors = formField?.errors || [];

  if (formSubmittedCount === 0) {
    return errors.filter((error) => error?.type !== ValidationActionType.REQUIRED);
  }

  return errors;
};

const getRenderedField = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, isAuthor: boolean): ChartMetaFormField => {
  const formField = formFieldMap.get(fieldName);

  if (!formField) return formField;

  const withDisabled = isAuthor ? { ...formField, isRequired: false } : { ...formField, disabled: true, isRequired: false };
  const fieldConfig = getFieldConfig(fieldName);
  const withMaxLength =
    (fieldConfig?.type === FormFieldInputType.TEXT_AREA || fieldConfig?.type === FormFieldInputType.TEXT_BOX) &&
    typeof fieldConfig.maxLength === 'number' &&
    withDisabled.onChange
      ? {
          ...withDisabled,
          onChange: (field: ChartMetaFormField, arg: unknown, ...rest: unknown[]) =>
            withDisabled.onChange(field, getTrimmedChangeEvent(arg, fieldConfig.maxLength), ...(rest as [unknown]))
        }
      : withDisabled;

  const fieldValue = String(withMaxLength.value ?? '').trim();
  const isCheckboxField = fieldConfig?.type === FormFieldInputType.CHECK_BOX;
  const isRadioField = fieldConfig?.type === FormFieldInputType.RADIO_CHOICE;
  if (!isCheckboxField && Array.isArray(withMaxLength.chartContent) && withMaxLength.chartContent.length > 0) {
    if (fieldValue) {
      // Selection is driven only by data from the API (saved/authored value).
      const hasMatchingSelection = withMaxLength.chartContent.some((item) => item.value === fieldValue && item.selected);
      if (!hasMatchingSelection) {
        return {
          ...withMaxLength,
          chartContent: withMaxLength.chartContent.map((item) => ({ ...item, selected: item.value === fieldValue }))
        };
      }
    } else if (isRadioField && withMaxLength.chartContent.some((item) => item.selected)) {
      // Radio with no API/saved value: never pre-select an option (e.g. Membranes).
      // Scoped to radios so dropdown placeholder items are left untouched.
      return {
        ...withMaxLength,
        chartContent: withMaxLength.chartContent.map((item) => ({ ...item, selected: false }))
      };
    }
  }

  return withMaxLength;
};

const hasFieldValue = (formField: ChartMetaFormField | undefined, fieldName?: FormField): boolean => {
  if (!formField) return false;

  const { value } = formField;

  if (Array.isArray(value)) return value.length > 0;

  const strVal = String(value ?? '').trim();
  return strVal.length > 0;
};

const isFocusedHistoryChecked = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField): boolean =>
  hasFieldValue(formFieldMap.get(fieldName), fieldName);

const isFocusedHistoryDetailsField = (fieldName: FormField): boolean =>
  Object.values(FOCUSED_HISTORY_DETAILS_MAP).includes(fieldName);

const getFieldWidth = (fieldName: FormField, row: FormField[]) => {
  if (isFocusedHistoryDetailsField(fieldName)) return '2o3';
  if (row.some((item) => FOCUSED_HISTORY_DETAILS_MAP[item])) return '1o3';
  if (row.includes(FormField.FETAL_ACTIVITY)) return '1o3';
  if (fieldName === FormField.INITIAL_COMMENTS) return UNIFORM_FIELD_WIDTH;
  if (row.length === 1 && getFieldConfig(fieldName)?.type === FormFieldInputType.TEXT_AREA) return '1o1';
  if (row.length === 2) return UNIFORM_FIELD_WIDTH;

  return UNIFORM_FIELD_WIDTH;
};

const getFieldOptions = (fieldConfig?: OBTriageFieldConfig) => {
  if (!fieldConfig) return undefined;

  const options: { displayHorizontal?: boolean; maxLength?: number } = {};

  if (fieldConfig.displayHorizontal) options.displayHorizontal = true;
  if (fieldConfig.maxLength) options.maxLength = fieldConfig.maxLength;

  return Object.keys(options).length ? options : undefined;
};

const withSelectPlaceholder = (formField: ChartMetaFormField | undefined): ChartMetaFormField | undefined => {
  // Placeholder text is handled visually via CSS when value is empty.
  return formField;
};

const renderRadioOptionWithInput = (
  formFieldMap: Map<string, ChartMetaFormField>,
  formSubmittedCount: number,
  isAuthor: boolean,
  unitField: ChartMetaFormField | undefined,
  optionValue: string,
  inputFieldName: FormField,
  siblingFieldNames: FormField[]
) => {
  const selectedUnit = String(unitField?.value || '').toLowerCase();
  const isSelected = selectedUnit === optionValue.toLowerCase();
  const inputFormField = formFieldMap.get(inputFieldName);
  const renderedInput = inputFormField
    ? { ...inputFormField, label: '', disabled: !isSelected || !isAuthor, value: isSelected ? String(inputFormField.value ?? '') : '' }
    : inputFormField;
  const inputId = inputFormField ? `field-input-${inputFieldName}` : undefined;

  return (
    <div className="ob-triage__radio-with-input" key={optionValue}>
      <label className="ob-triage__radio-option-label" htmlFor={inputId}>
        <input
          type="radio"
          id={undefined}
          checked={isSelected}
          disabled={!isAuthor}
          onChange={() => {
            unitField?.onChange?.(unitField, { target: { value: optionValue } });

            // Keep only the currently selected unit value and clear the sibling field(s).
            siblingFieldNames
              .filter((fieldName) => fieldName !== inputFieldName)
              .forEach((fieldName) => {
                const siblingField = formFieldMap.get(fieldName);
                siblingField?.onChange?.(siblingField, { target: { value: '' } });
              });
          }}
        />
        <span>{optionValue}</span>
      </label>
      <div className={`ob-triage__radio-option-input${!hasFieldValue(renderedInput, inputFieldName) ? ' ob-triage__number-placeholder' : ''}`}>
        <ErrorFormField formField={renderedInput} formSubmittedCount={formSubmittedCount} formFieldMap={formFieldMap} />
      </div>
    </div>
  );
};

const renderLMPRow = (formFieldMap: Map<string, ChartMetaFormField>, formSubmittedCount: number, isAuthor: boolean) => {
  const unitField = formFieldMap.get(FormField.LMP_HOW_LONG_AGO_UNIT);
  const label = getFieldConfig(FormField.LMP_HOW_LONG_AGO_UNIT)?.label;
  return (
    <ELSFlex className="u-flex-gutter-3x" key={FormField.LMP_HOW_LONG_AGO_UNIT} left wrap>
      <ELSFlexItem md="1o1" w="1o2">
        <div className="ob-triage__field">
          <span className="c-els-field__label">{label}</span>
          {renderRadioOptionWithInput(formFieldMap, formSubmittedCount, isAuthor, unitField, 'Days', FormField.LMP_DAYS, [FormField.LMP_DAYS, FormField.LMP_WEEKS])}
          {renderRadioOptionWithInput(formFieldMap, formSubmittedCount, isAuthor, unitField, 'Weeks', FormField.LMP_WEEKS, [FormField.LMP_DAYS, FormField.LMP_WEEKS])}
        </div>
      </ELSFlexItem>
      <ELSFlexItem md="1o1" w="1o2">
        <div className={!hasFieldValue(formFieldMap.get(FormField.CERTAINTY)) ? 'ob-triage__dropdown-placeholder' : undefined}>
          <ErrorFormField
            className="ob-triage__field"
            formField={withSelectPlaceholder(getRenderedField(formFieldMap, FormField.CERTAINTY, isAuthor))}
            formSubmittedCount={formSubmittedCount}
            formFieldMap={formFieldMap}
          />
        </div>
      </ELSFlexItem>
    </ELSFlex>
  );
};

const renderEDBRow = (formFieldMap: Map<string, ChartMetaFormField>, formSubmittedCount: number, isAuthor: boolean) => {
  const unitField = formFieldMap.get(FormField.EDB_FROM_NOW_UNIT);
  return (
    <ELSFlex className="u-flex-gutter-3x" key={FormField.EDB_FROM_NOW_UNIT} left wrap>
      <ELSFlexItem md="1o1" w="1o2">
        {renderRadioOptionWithInput(
          formFieldMap,
          formSubmittedCount,
          isAuthor,
          unitField,
          'Days from now',
          FormField.EDB_DAYS_FROM_NOW,
          [FormField.EDB_DAYS_FROM_NOW, FormField.EDB_WEEKS_FROM_NOW]
        )}
        {renderRadioOptionWithInput(
          formFieldMap,
          formSubmittedCount,
          isAuthor,
          unitField,
          'Weeks from now',
          FormField.EDB_WEEKS_FROM_NOW,
          [FormField.EDB_DAYS_FROM_NOW, FormField.EDB_WEEKS_FROM_NOW]
        )}
      </ELSFlexItem>
      <ELSFlexItem md="1o1" w="1o2">
        <ErrorFormField
          className="ob-triage__field"
          formField={getRenderedField(formFieldMap, FormField.CONFIRMED_BY_ULTRASOUND, isAuthor)}
          formSubmittedCount={formSubmittedCount}
          formFieldMap={formFieldMap}
          options={getFieldOptions(getFieldConfig(FormField.CONFIRMED_BY_ULTRASOUND))}
        />
        <div className="ob-triage__field">
          {(() => {
            const gestationalField = getRenderedField(formFieldMap, FormField.STEP2_GESTATIONAL_AGE, isAuthor);

            return (
          <ErrorFormField
            formField={gestationalField}
            formSubmittedCount={formSubmittedCount}
            formFieldMap={formFieldMap}
          />
            );
          })()}
          <span className="ob-triage__weeks-label">Weeks</span>
        </div>
      </ELSFlexItem>
    </ELSFlex>
  );
};

const renderFieldRow = (formFieldMap: Map<string, ChartMetaFormField>, formSubmittedCount: number, isAuthor: boolean, row: FormField[]) => {
  if (row.includes(FormField.LMP_HOW_LONG_AGO_UNIT)) {
    return renderLMPRow(formFieldMap, formSubmittedCount, isAuthor);
  }
  if (row.includes(FormField.LMP_DAYS) || row.includes(FormField.LMP_WEEKS)) {
    return null;
  }
  if (row.includes(FormField.EDB_FROM_NOW_UNIT)) {
    return renderEDBRow(formFieldMap, formSubmittedCount, isAuthor);
  }
  if (row.includes(FormField.EDB_DAYS_FROM_NOW) || row.includes(FormField.EDB_WEEKS_FROM_NOW)) {
    return null;
  }

  return (
    <ELSFlex
      className={`u-flex-gutter-3x${row.some((f) => f in FOCUSED_HISTORY_DETAILS_MAP) ? ' ob-triage__row--focused-history' : ''}`}
      key={row.join('-')}
      left
      wrap
    >
      {row.map((fieldName) => {
        const parentField = getFocusedHistoryParentField(fieldName);
        if (parentField && !isFocusedHistoryChecked(formFieldMap, parentField)) {
          return null;
        }

        const formField = getRenderedField(formFieldMap, fieldName, isAuthor);
        const fieldConfig = getFieldConfig(fieldName);

        if (formField?.inputType === FormFieldInputType.DATE) {
          const visibleErrors = getVisibleErrors(formField, formSubmittedCount);

          return (
            <ELSFlexItem key={fieldName} md="1o1" w={getFieldWidth(fieldName, row)}>
              <div className={["ob-triage__field", visibleErrors.length > 0 && 'chart-input__field--has-errors'].filter(Boolean).join(' ')}>
                <label className="c-els-field__label">
                  {formField.label}
                </label>
                <OBTriageDateField formField={formField} formSubmittedCount={formSubmittedCount} />
              </div>
            </ELSFlexItem>
          );
        }

        if (formField?.inputType === FormFieldInputType.TIME) {
          return (
            <ELSFlexItem key={fieldName} md="1o1" w={getFieldWidth(fieldName, row)}>
              <div className="ob-triage__field">
                <label className="c-els-field__label">
                  {formField.label}
                </label>
                <OBTriageTimeField formField={formField} />
              </div>
            </ELSFlexItem>
          );
        }

        return (
          <ELSFlexItem key={fieldName} md="1o1" w={getFieldWidth(fieldName, row)}>
            <div className={fieldConfig?.type === FormFieldInputType.DROPDOWN && !hasFieldValue(formField) ? 'ob-triage__dropdown-placeholder' : undefined}>
              <ErrorFormField
                className={['ob-triage__field', isFocusedHistoryDetailsField(fieldName) && 'ob-triage__focused-details'].filter(Boolean).join(' ')}
                formField={withSelectPlaceholder(formField)}
                formSubmittedCount={formSubmittedCount}
                options={getFieldOptions(fieldConfig)}
                formFieldMap={formFieldMap}
              />
              {fieldConfig?.type === FormFieldInputType.TEXT_AREA && fieldConfig.maxLength && (
                <div className="ob-triage__char-count">
                  {String(formField?.value ?? '').length}/{fieldConfig.maxLength} characters
                </div>
              )}
            </div>
          </ELSFlexItem>
        );
      })}
    </ELSFlex>
  );
};

const OBTriageView = ({ activeStep, canContinue, completedSteps = [], displayAuthoringData, enableDisplayRecordsButton, formFieldMap, formSubmittedCount, maxSelectableStep, isAuthor, onBackStep, onContinueStep, onSelectStep }: OBTriageViewProps) => {
  const activeStepConfig = STEP_CONFIGS[activeStep];
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === STEP_CONFIGS.length - 1;

  return (
    <div className="ob-triage">
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />
      <h1>{ViewTitle.OB_TRIAGE_RECORD}</h1>

      <div className="ob-triage__flow">
        {STEP_CONFIGS.map((stepConfig, stepIndex) => {
          const isActive = stepIndex === activeStep;
          const canAccessStep = !isAuthor || stepIndex <= maxSelectableStep;
          const isCompleted = Boolean(completedSteps[stepIndex]);

          return (
            <section className="ob-triage__step" key={stepConfig.title}>
              <button
                className={`ob-triage__step-header ${canAccessStep ? 'ob-triage__step-header--clickable' : 'ob-triage__step-header--locked'}`}
                disabled={!canAccessStep}
                onClick={() => onSelectStep(stepIndex)}
                type="button"
              >
                <span className={['ob-triage__step-index', isActive && 'ob-triage__step-index--active', isCompleted && !isActive && 'ob-triage__step-index--completed'].filter(Boolean).join(' ')}>
                  {isCompleted && !isActive ? <ELSIcon name="checkmark" size="1x" color="white" title="Completed" /> : stepIndex + 1}
                </span>
                <span className={`ob-triage__step-title${isActive ? ' ob-triage__step-title--active' : ''}`}>{stepConfig.title}</span>
              </button>

              {isActive && (
                <div className="ob-triage__panel">
                  {activeStepConfig.sections.map((section, sectionIndex) => (
                    <div className={`ob-triage__section ${sectionIndex > 0 ? 'ob-triage__section--divided' : ''}`} key={section.title}>
                      {!(sectionIndex === 0 && section.title === activeStepConfig.title) && <h4 className="ob-triage__section-title">{section.title}</h4>}
                      {section.rows.map((row) => renderFieldRow(formFieldMap, formSubmittedCount, isAuthor, row))}
                    </div>
                  ))}

                  <ChartPermission>
                    {({ allowSaveFragment }) =>
                      <div className="ob-triage__actions">
                        {!isFirstStep && (
                          <ELSButton
                            onClick={onBackStep}
                            text="Back"
                            type="secondary"
                            iconName="chevron-left"
                            iconPosition="left"
                            iconSize="1x"
                          />
                        )}

                        {isLastStep ? (
                          isAuthor ? (
                            <>
                              <ELSButton
                                isDisabled={!canContinue || !allowSaveFragment}
                                onClick={onContinueStep}
                                text="Save Records"
                              />
                              <ELSButton
                                type="secondary"
                                text="Return with data"
                                isDisabled={!enableDisplayRecordsButton}
                                onClick={displayAuthoringData}
                              />
                            </>
                          ) : null
                        ) : (
                          <ELSButton
                            isDisabled={!canContinue}
                            onClick={onContinueStep}
                            text={isAuthor ? 'Save & Next' : 'Continue'}
                            type="primary"
                            iconName="chevron-right"
                            iconPosition="right"
                            iconSize="1x"
                          />
                        )}
                      </div>
                    }
                  </ChartPermission>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

OBTriageView.displayName = 'OBTriageView';
export default OBTriageView;
